import {
  DashboardOutlined,
  DollarOutlined,
  FallOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import { Col, Row } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteTargets } from '../api/hooks';
import type { TargetCheck } from '../api/types';
import { StatCard } from '../components/StatCard';
import { percentOf, useTargetLabel, type TargetKind } from '../components/TargetCheck';
import { useSiteContext } from '../context/SiteContext';
import { formatMoney, formatNumber } from '../format';

const tone = (c: TargetCheck | null) =>
  c?.met === true ? 'success' : c?.met === false ? 'danger' : 'default';

/**
 * The progress of the site against the targets of the group and its intensity ratios over the
 * year to date (FR-PIL-10, FR-PIL-11). Hidden when there is nothing to follow and the role
 * cannot enter the activity.
 */
export function TargetsSection() {
  const { t } = useTranslation();
  const { siteId } = useSiteContext();
  const targetLabel = useTargetLabel();
  const { data: p } = useSiteTargets(siteId);
  if (!p) return null;
  const hasActivity = p.activity.months > 0;
  if (!p.target && !hasActivity && !p.canEditActivity) return null;

  const goal = (c: TargetCheck | null, kind: TargetKind) =>
    c
      ? t('dashboard.targets.goal', { target: targetLabel(kind, c.target) })
      : t('dashboard.targets.noGoal');
  const unit = p.productionUnit || t('activity.units');
  const cards: { key: string; node: ReactNode }[] = [
    {
      key: 'valorisation',
      node: (
        <StatCard
          icon={<RetweetOutlined />}
          label={t('dashboard.targets.valorisation')}
          value={percentOf(p.valorisationRate)}
          hint={goal(p.progress.valorisationRate, 'valorisationRate')}
          tone={tone(p.progress.valorisationRate)}
        />
      ),
    },
    {
      key: 'reduction',
      node: (
        <StatCard
          icon={<FallOutlined />}
          label={t('dashboard.targets.ddReduction', { year: p.year - 1 })}
          value={percentOf(p.ddReduction)}
          hint={`${t('dashboard.targets.ddHint', {
            current: formatNumber(p.ddGeneratedT),
            previous: formatNumber(p.ddGeneratedPreviousT),
          })} · ${goal(p.progress.ddReductionRate, 'ddReductionRate')}`}
          tone={tone(p.progress.ddReductionRate)}
        />
      ),
    },
  ];
  // the cost per tonne only for the roles that read the costs of the site (FS 4.3)
  if (p.costPerTonne !== null || p.progress.maxCostPerTonne) {
    cards.push({
      key: 'cost',
      node: (
        <StatCard
          icon={<DollarOutlined />}
          label={t('dashboard.targets.costPerTonne')}
          value={p.costPerTonne === null ? '—' : formatMoney(p.costPerTonne)}
          hint={goal(p.progress.maxCostPerTonne, 'maxCostPerTonne')}
          tone={tone(p.progress.maxCostPerTonne)}
        />
      ),
    });
  }
  cards.push({
    key: 'intensity',
    node: (
      <StatCard
        icon={<DashboardOutlined />}
        label={t('dashboard.targets.intensity', { unit })}
        value={hasActivity ? formatNumber(p.ratios.kgDdPerUnit, 2) : '—'}
        hint={
          hasActivity
            ? t('dashboard.targets.intensityHint', {
                employee: formatNumber(p.ratios.kgPerEmployeeMonth, 2),
                hours: formatNumber(p.ratios.kgPerThousandHours, 2),
              })
            : t('dashboard.targets.noActivity')
        }
        to="/settings/activity"
      />
    ),
  });
  const span = 24 / cards.length;
  return (
    <>
      <div className="wm-section-title" style={{ marginTop: 24 }}>
        {t('dashboard.targets.title', { year: p.year })}
      </div>
      <Row gutter={[16, 16]}>
        {cards.map((c) => (
          <Col key={c.key} xs={24} sm={12} xl={span}>
            {c.node}
          </Col>
        ))}
      </Row>
    </>
  );
}
